import confetti from 'canvas-confetti';
import { Beer, Edit2, MessageCircle, Navigation, Save, Send, Trash2, Utensils, X } from 'lucide-react';
import React, { useState } from 'react';
import { STATUS_CONFIG } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import { useDataStore } from '../stores/useDataStore';
import { Category, PopulatedShout, PopulatedVenue, RecommendationStatus } from '../types';
import { formatRelativeDate } from '../utils/date';
import { StatusStamp } from './StatusStamp';
import { FollowButton } from './profile/FollowButton';

interface DrawerProps {
  venue: PopulatedVenue | null;
  onClose: () => void;
}

const ShoutCard: React.FC<{
    shout: PopulatedShout;
    isMe: boolean;
    currentUserFriends: string[];
    onUpdate: (id: string, status: RecommendationStatus, description: string, category?: Category) => Promise<void>;
    onGoodShout: (shoutId: string, message: string) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
    venueCategory: Category;
    currentUserId?: string;
}> = ({ shout, isMe, currentUserFriends, onUpdate, onGoodShout, onDelete, venueCategory, currentUserId }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editStatus, setEditStatus] = useState(shout.status);
    const [editDesc, setEditDesc] = useState(shout.description);
    const [editCategory, setEditCategory] = useState(venueCategory);
    
    const [isShouting, setIsShouting] = useState(false);
    const [shoutMsg, setShoutMsg] = useState('');

    const [isDeleting, setIsDeleting] = useState(false);

    // For friends, we check if the shout user is in my friends list
    // Note: currentUserFriends is an array of UIDs
    const isFriend = currentUserFriends.includes(shout.user.uid);
    
    const hasGoodShouted = currentUserId && shout.goodShouts.some(gs => gs.userId === currentUserId);

    // Follow Logic
    const { currentUser } = useAuth();



    const handleSaveEdit = async () => {
        await onUpdate(shout.id, editStatus, editDesc, editCategory);
        setIsEditing(false);
    };

    const handleDelete = async () => {
        await onDelete(shout.id);
    };

    const handleSendShoutOut = async (e: React.MouseEvent) => {
        if (!shoutMsg.trim()) return;

        // Confetti Explosion from Click!
        const x = e.clientX / window.innerWidth;
        const y = e.clientY / window.innerHeight;

        confetti({
            particleCount: 100,
            spread: 70,
            origin: { x, y },
            colors: ['#D64045', '#FEFBF6', '#374151'] // Brand colors
        });

        await onGoodShout(shout.id, shoutMsg);
        setShoutMsg('');
        setIsShouting(false);
    };

    return (
        <div className="relative pl-4 border-l-2 border-brand-cream-dark">
            {/* User Header */}
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-3 min-w-0">
                    <img 
                        src={shout.user.photoURL} 
                        alt={shout.user.displayName} 
                        className="w-10 h-10 rounded-full object-cover border border-brand-cream-dark"
                    />
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h4 className="font-bold text-brand-dark">{isMe ? 'You' : shout.user.displayName}</h4>
                            
                            {/* Follow Button (for friends) */}
                            {isFriend && !isMe && currentUser && (
                                <FollowButton 
                                    friend={shout.user} 
                                    user={currentUser}
                                    className="p-1 rounded-full text-brand-dark-light hover:text-brand-dark" 
                                    iconSize={12}
                                />
                            )}
                        </div>
                        <p className="text-xs text-brand-dark-light">@{shout.user.username}</p>
                    </div>
                </div>
                
                {/* Action Buttons or Badge */}
                <div className="flex items-center gap-2 shrink-0">
                    {isMe && !isEditing && !isDeleting && (
                        <>
                            <button onClick={() => setIsEditing(true)} className="p-1.5 text-brand-dark-light hover:text-brand-dark hover:bg-brand-cream-dark rounded-full">
                                <Edit2 size={14} />
                            </button>
                            <button onClick={() => setIsDeleting(true)} className="p-1.5 text-brand-dark-light hover:text-brand-red hover:bg-brand-cream-dark rounded-full">
                                <Trash2 size={14} />
                            </button>
                        </>
                    )}

                    {!isEditing && !isDeleting ? (
                         <StatusStamp status={shout.status} size="md" animate />
                    ) : (
                        <div className="flex gap-1">
                            {(Object.values(RecommendationStatus) as RecommendationStatus[]).map(s => {
                                const StatusIcon = STATUS_CONFIG[s].Icon;
                                return (
                                    <button 
                                        key={s} 
                                        onClick={() => setEditStatus(s)}
                                        aria-label={STATUS_CONFIG[s].label}
                                        className={`p-1.5 rounded-full ${editStatus === s ? 'bg-brand-dark text-white scale-110' : 'bg-brand-cream-dark text-brand-dark-light'}`}
                                    >
                                        <StatusIcon size={12} strokeWidth={2.5} />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Shout Content */}
            {isEditing ? (
                <div className="mt-2 space-y-3">
                    {/* Category Selector */}
                    <div className="flex gap-2">
                        <button 
                            onClick={() => setEditCategory(Category.FOOD)}
                            className={`flex-1 py-1.5 rounded-lg text-xs font-bold border ${editCategory === Category.FOOD ? 'bg-brand-dark text-white border-brand-dark' : 'bg-white text-brand-dark-light border-brand-cream-dark'}`}
                        >
                            Food
                        </button>
                        <button 
                            onClick={() => setEditCategory(Category.DRINKS)}
                            className={`flex-1 py-1.5 rounded-lg text-xs font-bold border ${editCategory === Category.DRINKS ? 'bg-brand-dark text-white border-brand-dark' : 'bg-white text-brand-dark-light border-brand-cream-dark'}`}
                        >
                            Drinks
                        </button>
                    </div>

                    <textarea 
                        value={editDesc}
                        onChange={e => setEditDesc(e.target.value)}
                        className="w-full p-2 text-sm border-2 border-brand-cream-dark rounded-lg focus:border-brand-dark outline-none bg-brand-cream"
                        rows={3}
                    />
                    <div className="flex justify-end gap-2 mt-2">
                         <button onClick={() => setIsEditing(false)} className="px-3 py-1.5 text-xs font-bold text-brand-dark-light hover:text-brand-dark">Cancel</button>
                         <button onClick={handleSaveEdit} className="px-3 py-1.5 text-xs font-bold bg-brand-dark text-white rounded-lg flex items-center gap-1">
                            <Save size={12} /> Save
                         </button>
                    </div>
                </div>
            ) : isDeleting ? (
                <div className="mt-2 p-3 bg-brand-red/10 border border-brand-red/20 rounded-xl animate-in fade-in">
                    <p className="text-sm font-bold text-brand-red mb-2">Are you sure?</p>
                    <p className="text-xs text-brand-dark-light mb-3">This cannot be undone.</p>
                    <div className="flex justify-end gap-2">
                         <button onClick={() => setIsDeleting(false)} className="px-3 py-1.5 text-xs font-bold text-brand-dark-light hover:text-brand-dark">Cancel</button>
                         <button onClick={handleDelete} className="px-3 py-1.5 text-xs font-bold bg-brand-red text-white rounded-lg flex items-center gap-1 hover:bg-brand-red-hover">
                            Delete
                         </button>
                    </div>
                </div>
            ) : (
                <>
                    <p className="text-brand-dark leading-relaxed bg-brand-cream-dark/50 p-3 rounded-xl rounded-tl-none ml-1 font-medium">
                        "{shout.description}"
                    </p>
                    
                    {/* Actions Bar */}
                    <div className="flex items-center justify-between gap-2 mt-2 pr-2">
                        <p className="text-xs text-brand-dark-light">
                            {formatRelativeDate(shout.createdAt)}
                        </p>
                        {!isMe && (
                            <button
                                onClick={() => !hasGoodShouted && setIsShouting(!isShouting)}
                                disabled={!!hasGoodShouted}
                                className={`ml-auto text-xs font-bold flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 transition-all active:scale-95 ${isShouting ? 'bg-brand-red text-white border-brand-red' : 'border-brand-red text-brand-red hover:bg-brand-red/10'} ${hasGoodShouted ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <MessageCircle size={14} strokeWidth={2.5} />
                                <span>{hasGoodShouted ? 'Already Thanked' : (isShouting ? 'Cancel' : 'Good Shout')}</span>
                                {shout.goodShouts.length > 0 && (
                                    <>
                                        <span className="w-px h-3 bg-current opacity-40" />
                                        <span>{shout.goodShouts.length}</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>

                    {/* ShoutOut Input */}
                    {isShouting && (
                        <div className="mt-2 flex gap-2 animate-in fade-in slide-in-from-top-2">
                            <input 
                                type="text" 
                                value={shoutMsg}
                                onChange={e => setShoutMsg(e.target.value)}
                                placeholder={`Say thanks to ${shout.user.displayName.split(' ')[0]}...`}
                                className="flex-1 px-3 py-2 bg-white border border-brand-cream-dark rounded-full text-sm outline-none focus:border-brand-red"
                                autoFocus
                            />
                            <button 
                                onClick={handleSendShoutOut}
                                disabled={!shoutMsg.trim()}
                                className="p-2 bg-brand-red text-white rounded-full shadow-md disabled:opacity-50 hover:bg-brand-red-hover"
                            >
                                <Send size={16} />
                            </button>
                        </div>
                    )}

                    {/* Good Shouts List */}
                    {shout.goodShouts.length > 0 && (
                        <div className="mt-3 ml-2 space-y-2">
                            {shout.goodShouts.map(gs => (
                                <div key={gs.id} className="flex items-start gap-2">
                                    <img src={gs.user.photoURL} className="w-5 h-5 rounded-full mt-0.5" />
                                    <div className="bg-brand-cream-dark px-3 py-1.5 rounded-xl rounded-tl-none">
                                        <p className="text-xs text-brand-dark">
                                            <span className="font-bold mr-1">{gs.user.displayName}</span>
                                            {gs.message}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export const Drawer: React.FC<DrawerProps> = ({ venue, onClose }) => {
  const { currentUser } = useAuth();
  const { updateShout, addGoodShout, deleteShout } = useDataStore();

  if (!venue) {
      return (
        <div data-testid="drawer" className="fixed inset-x-0 bottom-0 z-50 flex flex-col max-h-[85vh] bg-white shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.15)] rounded-t-3xl transition-transform duration-300 animate-in slide-in-from-bottom">
            <div className="w-full flex justify-center pt-3 pb-1 cursor-pointer" onClick={onClose}>
                <div className="w-12 h-1.5 bg-brand-cream-dark rounded-full"></div>
            </div>
            <div className="px-6 pb-4 pt-2 flex items-start justify-between border-b border-brand-cream-dark">
                <div className="h-16 w-48 bg-brand-cream animate-pulse rounded-xl"></div>
                <button 
                    onClick={onClose}
                    data-testid="drawer-close"
                    className="p-2 bg-brand-cream-dark rounded-full hover:bg-brand-cream transition-colors"
                >
                    <X size={20} className="text-brand-dark-light" />
                </button>
            </div>
            <div className="p-6 h-64 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-brand-dark border-t-transparent rounded-full animate-spin"></div>
            </div>
        </div>
      );
  }

  const isFood = venue.category === Category.FOOD;

  // "Mates rate this" summary: who shouted about this venue.
  const iShouted = !!currentUser && venue.shouts.some(s => s.user.uid === currentUser.uid);
  const otherCount = venue.shouts.filter(s => !currentUser || s.user.uid !== currentUser.uid).length;
  const mateWord = otherCount === 1 ? 'mate' : 'mates';
  const ratersLabel = iShouted
    ? (otherCount === 0 ? 'You rate this' : `You + ${otherCount} ${mateWord} rate this`)
    : `${otherCount} ${mateWord} ${otherCount === 1 ? 'rates' : 'rate'} this`;

  return (
    <div data-testid="drawer" className="fixed inset-x-0 bottom-0 z-50 flex flex-col max-h-[85vh] bg-white shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.15)] rounded-t-3xl transition-transform duration-300 animate-in slide-in-from-bottom">
      
      {/* Handle Bar */}
      <div className="w-full flex justify-center pt-3 pb-1 cursor-pointer" onClick={onClose}>
        <div className="w-12 h-1.5 bg-brand-cream-dark rounded-full"></div>
      </div>

      {/* Header */}
      <div className="px-6 pb-4 pt-2 flex items-start gap-4 border-b border-brand-cream-dark">
        {/* Category Icon */}
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 ${isFood ? 'bg-cat-food-bg text-cat-food-fg' : 'bg-cat-drinks-bg text-cat-drinks-fg'}`}>
             {isFood ? <Utensils size={32} strokeWidth={2} /> : <Beer size={32} strokeWidth={2} />}
        </div>

        <div className="flex-1 pt-1">
          <div className="flex justify-between items-start">
            <div>
                <h2 className="font-display text-2xl font-extrabold text-brand-dark leading-tight tracking-tight">{venue.name}</h2>
                <div className="flex items-center gap-2 mt-1.5">
                    {venue.shouts.length > 0 && (
                        <div className="flex -space-x-2 shrink-0">
                            {venue.shouts.slice(0, 4).map((shout) => (
                                <img
                                    key={shout.id}
                                    src={shout.user.photoURL}
                                    alt={shout.user.displayName}
                                    className="w-6 h-6 rounded-full object-cover ring-2 ring-brand-cream bg-brand-cream"
                                />
                            ))}
                            {venue.shouts.length > 4 && (
                                <span className="w-6 h-6 rounded-full ring-2 ring-brand-cream bg-brand-cream-dark text-brand-dark text-[10px] font-bold flex items-center justify-center">
                                    +{venue.shouts.length - 4}
                                </span>
                            )}
                        </div>
                    )}
                    <p className="text-brand-dark-light text-sm">
                        {ratersLabel}
                        {venue.isCustom && <span className="text-xs italic ml-2 opacity-70 whitespace-nowrap">• Custom Pin</span>}
                    </p>
                </div>
            </div>
            <button 
                onClick={onClose}
                data-testid="drawer-close"
                className="p-2 bg-brand-cream-dark rounded-full hover:bg-brand-cream transition-colors"
            >
                <X size={20} className="text-brand-dark-light" />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6 no-scrollbar bg-white">
        <div className="space-y-6">
          {venue.shouts.map((shout) => (
            <ShoutCard 
                key={shout.id}
                shout={shout}
                isMe={currentUser ? shout.userId === currentUser.uid : false}
                currentUserFriends={currentUser ? currentUser.friends : []}
                onUpdate={async (id, status, desc, cat) => {
                    if (currentUser) {
                         try {
                            await updateShout(currentUser, id, status, desc, cat);
                         } catch (e) {
                             // Error is handled in store (state.error) and displayed via App toast
                             // We catch here to prevent unhandled rejection
                         }
                    }
                }}
                onGoodShout={async (id, msg) => {
                    if (currentUser) await addGoodShout(currentUser, id, msg);
                }}
                onDelete={async (id) => {
                    if (currentUser) {
                        await deleteShout(currentUser, id);
                        if (venue.shouts.length === 1) {
                            onClose();
                        }
                    }
                }}
                venueCategory={venue.category}
                currentUserId={currentUser?.uid}
            />
          ))}
        </div>
      </div>
      
      {/* Action Footer (Sticky) */}
      <div className="p-4 border-t border-brand-cream-dark bg-white pb-8">
         <button 
            onClick={() => {
                const url = `https://www.google.com/maps/dir/?api=1&destination=${venue.lat},${venue.lng}`;
                window.open(url, '_blank');
            }}
            className="w-full py-3 border-2 border-brand-dark bg-transparent text-brand-dark rounded-xl font-bold transition active:scale-95 hover:bg-brand-cream flex items-center justify-center gap-2"
         >
            <Navigation size={18} strokeWidth={2.5} />
            Directions
         </button>
      </div>
    </div>
  );
};